import React, { createContext, useState, useEffect, useContext } from 'react';
import { User, AuthContextType } from '../types/auth.types';
import * as authService from '../services/auth.service';

// Default context values
const defaultContext: AuthContextType = {
  isAuthenticated: false,
  isAdmin: false,
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {}
};

// Create the Auth context
const AuthContext = createContext<AuthContextType>(defaultContext);

// Create the Auth provider component
export const AuthProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if the user is already logged in on component mount
  useEffect(() => {
    const checkLoggedIn = async () => {
      try {
        // Check if we have a token in localStorage
        const token = localStorage.getItem('authToken');
        
        if (!token) {
          setLoading(false);
          return;
        }
        
        // Set token in axios headers
        authService.setAuthToken(token);
        
        // Get current user
        const response = await authService.getCurrentUser();
        
        if (response.success && response.data) {
          setUser(response.data);
        }
      } catch (err) {
        // Clear token if invalid
        authService.setAuthToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkLoggedIn();
  }, []);

  // Handle user login
  const login = async (email: string, password: string) => {
    try {
      console.log('AuthContext: Calling login service');
      const response = await authService.login(email, password);
      console.log('AuthContext: Login response:', response);
      
      if (response.success && response.token) {
        // Save token and set headers
        authService.setAuthToken(response.token);
        console.log('AuthContext: Token saved, setting user');
        
        // Set user state
        if (response.data) {
          setUser(response.data);
          console.log('AuthContext: User set:', response.data);
        } else {
          console.error('AuthContext: No user data in response');
          // Try to fetch user data
          try {
            const userResponse = await authService.getCurrentUser();
            if (userResponse.success && userResponse.data) {
              setUser(userResponse.data);
              console.log('AuthContext: User fetched separately:', userResponse.data);
            }
          } catch (fetchErr) {
            console.error('AuthContext: Failed to fetch user data:', fetchErr);
          }
        }
      } else {
        console.error('AuthContext: Login failed - no success or token in response');
        throw new Error('Login failed');
      }
    } catch (err) {
      console.error('AuthContext: Login error:', err);
      // Clear any existing token
      authService.setAuthToken(null);
      throw err;
    }
  };

  // Handle user logout
  const logout = async () => {
    try {
      await authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear token and user state
      authService.setAuthToken(null);
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        user,
        loading,
        login,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the Auth context
export const useAuth = () => useContext(AuthContext);

export default AuthContext; 